import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { PageHead } from "./Layout";

const copyText = (text: string, ok = "Copied") =>
  navigator.clipboard.writeText(text).then(() => toast.success(ok), () => toast.error("Copy failed — select and copy manually"));

function CodeBox({ code, id }: { code: string; id: string }) {
  const copy = () => copyText(code);
  return (
    <div className="relative">
      <pre className="bg-[#0f1f1d] text-emerald-100 text-xs font-mono rounded-lg p-4 pr-14 overflow-x-auto whitespace-pre-wrap break-all" data-testid={`${id}-code`}>{code}</pre>
      <Button size="icon" variant="secondary" className="absolute top-2 right-2 h-9 w-9" onClick={copy} aria-label="Copy" data-testid={`${id}-copy-button`}><Copy className="h-4 w-4" /></Button>
    </div>
  );
}

function SlugEditor({ current, origin, onSaved }: { current: string; origin: string; onSaved: () => void }) {
  const [v, setV] = useState(current);
  const [check, setCheck] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const s = v.trim().toLowerCase();
  const valid = check && check.slug === s ? check : null;
  useEffect(() => {
    if (s === current) { setCheck(null); return undefined; }
    const t = setTimeout(() => { api.get("/orgs/me/slug-check", { params: { slug: s } }).then((r) => setCheck(r.data)).catch(() => setCheck(null)); }, 300);
    return () => { clearTimeout(t); };
  }, [s, current]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/orgs/me/slug", { slug: s }); toast.success("Quote link updated — your old link keeps working"); onSaved(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5 pt-2 border-t" data-testid="slug-editor">
      <Label className="text-xs" htmlFor="slug-input">Customize your link</Label>
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex items-center flex-1 min-w-0 rounded-md border bg-background">
          <span className="pl-3 text-xs font-mono text-muted-foreground whitespace-nowrap">/q/</span>
          <Input id="slug-input" className="border-0 font-mono text-sm focus-visible:ring-0" maxLength={40} value={v} onChange={(e) => setV(e.target.value.toLowerCase())} data-testid="slug-input" />
        </div>
        <Button size="sm" disabled={busy || s === current || !valid?.available} onClick={save} data-testid="slug-save">Save link</Button>
      </div>
      <p className="text-xs font-mono break-all text-muted-foreground" data-testid="slug-preview">{origin}/q/{s || "…"}</p>
      {valid && <p className={`text-xs ${valid.available ? "text-primary" : "text-destructive"}`} data-testid="slug-status">{valid.available ? <><Check className="inline h-3 w-3 mr-1" />Available</> : `Taken or not allowed: ${valid.reason}`}</p>}
      <p className="text-[11px] text-muted-foreground">Links you've already shared keep working and forward to the new one.</p>
    </div>
  );
}

function HostedLink({ url, name }: { url: string; name: string }) {
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const share = () => navigator.share({ title: name, text: "Get an instant estimate", url }).catch(() => undefined);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono text-xs break-all" data-testid="hosted-url">{url}</span>
        <Button variant="outline" size="sm" onClick={() => copyText(url, "Link copied")} data-testid="hosted-link-copy"><Copy className="h-3.5 w-3.5 mr-1" />Copy link</Button>
        {canShare && <Button variant="outline" size="sm" onClick={share} data-testid="hosted-link-share"><Share2 className="h-3.5 w-3.5 mr-1" />Share</Button>}
        <Button variant="outline" size="sm" asChild data-testid="open-hosted-button"><a href={url} target="_blank" rel="noreferrer">Open test page <ExternalLink className="h-3.5 w-3.5 ml-1" /></a></Button>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="hosted-link-tip">Share this link anywhere: text replies, your Facebook page, your Google Business Profile, your email signature.</p>
    </div>
  );
}

export default function Install() {
  const [w, setW] = useState<any>(null);
  const [orgName, setOrgName] = useState("");
  const [domains, setDomains] = useState("");
  const load = () => api.get("/orgs/me/widget").then(({ data }) => { setW(data); setDomains((data.allowed_domains || []).join("\n")); });
  useEffect(() => { load(); api.get("/orgs/me").then((r) => setOrgName(r.data.name || "")).catch(() => undefined); }, []);
  if (!w) return <p className="text-muted-foreground">Loading…</p>;
  const origin = window.location.origin;
  const embedUrl = `${origin}/embed/${w.public_id}`;
  const hostedUrl = `${origin}/q/${w.hosted_slug}`;
  const iframe = `<iframe id="iqw-frame"\n  src="${embedUrl}"\n  title="Get an instant service estimate"\n  loading="lazy"\n  allow="geolocation"\n  style="width:100%;min-height:680px;border:0;border-radius:12px"\n></iframe>\n<script>\n(function(){var f=document.getElementById("iqw-frame");\nwindow.addEventListener("message",function(e){if(e.source!==f.contentWindow||e.origin!=="${origin}")return;var d=e.data||{};\nif(d.type==="iqw:resize"&&typeof d.height==="number")f.style.height=Math.max(680,d.height)+"px";\nvar t=f.getBoundingClientRect().top;\nif(d.type==="iqw:step"&&t<0)window.scrollBy(0,t-12);\nif(d.type==="iqw:field"&&typeof d.offset==="number")window.scrollBy(0,t+d.offset-12);});})();\n</script>`;
  const button = `<a href="${hostedUrl}" target="_blank" rel="noopener"\n  style="display:inline-block;padding:14px 22px;border-radius:10px;background:#0F766E;color:#fff;font:600 16px sans-serif;text-decoration:none">\n  ${w.cta_text}\n</a>`;
  const patch = async (body: any) => {
    try { await api.patch("/orgs/me/widget", body); toast.success("Saved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fade-up">
      <PageHead title="Install" sub="Paste one snippet into your website builder (WordPress, Wix, Squarespace, Webflow → “Custom HTML” / “Embed” block)." />
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-8">
          <section className="bg-card border rounded-xl p-6 space-y-3">
            <h2 className="font-bold">1 · Inline widget (iframe)</h2>
            <CodeBox code={iframe} id="iframe-snippet" />
            <p className="text-xs text-muted-foreground">Runs in an isolated frame, so your site’s CSS can’t break it. The small script is optional but recommended: it sizes the frame to its content and keeps each step and the address field in view on phones (it only accepts messages from {origin}). If your site uses a Content-Security-Policy, add <code className="font-mono">{origin}</code> to <code className="font-mono">frame-src</code>.</p>
          </section>
          <section className="bg-card border rounded-xl p-6 space-y-3">
            <h2 className="font-bold">2 · Button linking to your hosted quote page</h2>
            <CodeBox code={button} id="button-snippet" />
            <HostedLink url={hostedUrl} name={orgName} />
            <SlugEditor key={w.hosted_slug} current={w.hosted_slug} origin={origin} onSaved={load} />
            <p className="text-xs text-muted-foreground">The hosted page also works where iframes are blocked. Test visits count as real map sessions.</p>
          </section>
          <section className="bg-card border rounded-xl p-6 space-y-5">
            <h2 className="font-bold">Controls</h2>
            <label className="flex items-center justify-between gap-4">
              <span><span className="font-medium text-sm">Widget enabled</span><br /><span className="text-xs text-muted-foreground">Turn off to instantly disconnect the widget everywhere it’s installed.</span></span>
              <Switch checked={w.active} onCheckedChange={(v) => patch({ active: v })} data-testid="widget-active-switch" />
            </label>
            <div className="space-y-1.5">
              <Label className="text-xs">Allowed website domains (one per line, optional)</Label>
              <Textarea rows={3} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder={"yourcompany.com\n*.yourcompany.com"} className="font-mono" data-testid="allowed-domains-input" />
              <p className="text-[11px] text-muted-foreground">When set, the inline widget only starts on these sites. This is a soft check (browsers don’t always report the parent site) — it is not a security boundary.</p>
              <Button size="sm" variant="outline" onClick={() => patch({ allowed_domains: domains.split(/\s+/).filter(Boolean) })} data-testid="allowed-domains-save">Save domains</Button>
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="install-allowance-note">Your plan includes a monthly allowance of satellite map sessions (see Overview for usage and reset date). When it’s used up, homeowners automatically get the manual quote form — leads are never blocked.</p>
          </section>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Test it here (same renderer as production)</p>
          <iframe title="Widget test" src={`${embedUrl}?preview=1`} className="w-full h-[680px] rounded-xl border bg-white" data-testid="install-preview-iframe" />
        </div>
      </div>
    </div>
  );
}
